import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { DatePicker } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import layout from "../../actprep-ui/constants/layout";
import ContentBox from "../../actprep-ui/components/ContentBox";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import api from "../../actprep-ui/utils/api";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";
import RolesTab from "./RolesTab";
import HierarchyTab from "./HierarchyTab";
import { formatPhoneNumber } from "../../actprep-ui/utils/formatting";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coach-tabpanel-${index}`}
      aria-labelledby={`coach-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function CoachManagement({ coachId, onCoachUpdated }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    parseInt(searchParams.get("tab")) || 0
  );
  const [coachUpdated, setCoachUpdated] = useState(false);

  // Get asOf date from URL params or default to today
  const asOfParam = searchParams.get("asOf");
  const [asOfDate, setAsOfDate] = useState(
    asOfParam ? dayjs(asOfParam) : dayjs()
  );

  // Fetch coach profile data
  const {
    data: coachData,
    isLoading: coachLoading,
    refetch: refetchCoach,
  } = useGet(apiUrls.coaching.profiles(coachId), {
    enabled: !!coachId,
    params: {
      keep_related: true,
      exclude: ["user_profile__user"],
    },
  });

  const coach = coachData?.data;

  const handleActiveStatusChange = async (event) => {
    const newActiveStatus = event.target.checked;

    try {
      const response = await api.put(apiUrls.coaching.profiles(coachId), {
        is_active: newActiveStatus,
      });

      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason || "Failed to update coach status"
        );
        return;
      }

      showSuccessSnackbar(
        `Coach ${newActiveStatus ? "activated" : "deactivated"} successfully!`
      );

      // Refetch coach data to update the UI
      refetchCoach();
      setCoachUpdated(true);
    } catch (error) {
      console.error("Error updating coach status:", error);
      const errorMessage =
        error.response?.data?.reason || "Failed to update coach status";
      showErrorSnackbar(errorMessage);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", newValue.toString());
    setSearchParams(newParams);
  };

  const handleAsOfDateChange = (newDate) => {
    setAsOfDate(newDate);
    const newParams = new URLSearchParams(searchParams);
    if (newDate) {
      newParams.set("asOf", newDate.format("YYYY-MM-DD"));
    } else {
      newParams.delete("asOf");
    }
    setSearchParams(newParams);
  };

  const handleBackToTeamList = (shouldRefresh = false) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("coachId");
    newParams.delete("tab");
    newParams.delete("asOf");

    // Add refresh trigger if data was changed
    if (shouldRefresh) {
      newParams.set("refresh", Date.now().toString());
    }
    if (onCoachUpdated && coachUpdated) {
      onCoachUpdated();
    }

    setSearchParams(newParams);
  };

  const coachInitials = useMemo(() => {
    if (!coach) return "";
    const firstName = coach.first_name || coach.user?.first_name || "";
    const lastName = coach.last_name || coach.user?.last_name || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }, [coach]);

  if (coachLoading) {
    return (
      <Grid
        sx={{
          mt: `${layout.navBarHeight + 1}rem`,
          mb: 4,
        }}
        size={12}
      >
        <Typography variant="h5" fontWeight="bold" my={2} color="primary">
          Loading coach...
        </Typography>
      </Grid>
    );
  }

  if (!coach) {
    return (
      <Grid
        sx={{
          mt: `${layout.navBarHeight + 1}rem`,
          mb: 4,
        }}
        size={12}
      >
        <Typography variant="h5" fontWeight="bold" my={2} color="primary">
          Coach not found
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid
      sx={{
        mt: `${layout.navBarHeight + 1}rem`,
        mb: 4,
      }}
      size={12}
    >
      <Stack direction="row" alignItems="center" spacing={2} my={2}>
        <IconButton onClick={() => handleBackToTeamList(true)} color="primary">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight="bold" color="primary">
          Coach Management
        </Typography>
      </Stack>

      {/* Header with Coach Identity and As Of Date Picker */}
      <ContentBox sx={{ mb: 3 }}>
        <Card elevation={0}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              {/* Coach Identity */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{ width: 56, height: 56 }}
                  src={coach.user_profile?.profile_pic}
                >
                  {coachInitials}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {coach.user?.first_name} {coach.user?.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {coach.email}
                  </Typography>
                  {coach.user_profile?.phone_number && (
                    <Typography variant="body2" color="text.secondary">
                      {formatPhoneNumber(coach.user_profile.phone_number)}
                    </Typography>
                  )}
                  {coach.team_name && (
                    <Typography variant="body2" color="primary">
                      Team: {coach.team_name}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* As Of Date Picker and Active Status */}
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={coach?.is_active ?? false}
                      onChange={handleActiveStatusChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Active Coach
                    </Typography>
                  }
                />
                <DatePicker
                  label="As of Date"
                  value={asOfDate}
                  onChange={handleAsOfDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </ContentBox>

      {/* Tabs */}
      <ContentBox>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="coach management tabs"
          >
            <Tab
              label="Roles"
              id="coach-tab-0"
              aria-controls="coach-tabpanel-0"
            />
            <Tab
              label="Hierarchy"
              id="coach-tab-1"
              aria-controls="coach-tabpanel-1"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <RolesTab coach={coach} asOfDate={asOfDate} />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <HierarchyTab coach={coach} asOfDate={asOfDate} />
        </TabPanel>
      </ContentBox>
    </Grid>
  );
}
